<script lang="ts">
  import Main from "@retailer/svelte/Shared/QuickOrderList/Main.svelte";
  import Sidebar from "@retailer/svelte/Shared/QuickOrderList/Sidebar.svelte";
  import NewListModal from "@retailer/svelte/Shared/QuickOrderList/NewListModal.svelte";
  import PageTitle from "@shared/svelte/Components/PageTitle.svelte";
  import type {
    QuickOrderListAdParams,
    QuickOrderListDetail,
    QuickOrderListIndex,
    QuickOrderListFilters,
    ListType,
  } from "@shared/types/QuickOrderList";
  import QuickOrderListProductsStore from "@retailer/svelte/stores/QuickOrderListProductsStore";
  import SelfFetchingCardCarousel from "@shared/svelte/Components/Carousel/SelfFetchingCardCarousel.svelte";
  import type { PageData } from "@shared/types/PageData";
  import { determineListAdParams, listCarouselLink } from "@shared/services/list_ads_service";
  import { OnLayoutChange } from "@shared/svelte/Directives/OnLayoutChange.js";
  import { smartFilter } from "@retailer/svelte/stores/QuickOrderListSmartFilterStore";
  import { params } from "@roxi/routify";

  import type { ProductListEntity } from "@shared/services/event_tracking/entities";
  import type { RetailerType } from "@shared/types/Retailer";

  export let retailer: RetailerType;
  export let callbacks;
  export let filters: QuickOrderListFilters;
  export let list: QuickOrderListDetail;
  export let listType: ListType;
  export let lists: QuickOrderListIndex[];
  export let page: PageData;
  export let showLoading: boolean = false;
  export let smartFilterDistributor: string = undefined;

  let isMobile: boolean = false;
  let limitedOrderingRetailer = false;
  let newListName = "";
  let showModal: boolean = false;
  let sidebar: boolean;
  let storedDistributor = null;
  let productListEntity: ProductListEntity;

  let showCarousel: boolean = false;
  let carouselKey: string = "";
  let carouselLink: string = "";
  let listAdParams: QuickOrderListAdParams | null = null;
  $: {
    listAdParams = determineListAdParams($params, filters);
    showCarousel = !!listAdParams && $QuickOrderListProductsStore.length > 0;
    carouselKey = list.id + JSON.stringify(listAdParams);
    carouselLink = listCarouselLink(listAdParams);
  }

  $: {
    list = list;
    page = page;
    if (smartFilterDistributor) {
      const filterId = parseInt(smartFilterDistributor);
      let distributors = filters.smartFilterDistributors.filter(
        distributor => distributor.id === filterId,
      );
      if (distributors.length > 0 && distributors[0].name) {
        storedDistributor = { id: filterId, name: distributors[0].name };
      }
    } else {
      storedDistributor = null;
    }
    smartFilter.set({ distributor: storedDistributor });
    sidebar = false;
  }
  $: limitedOrderingRetailer = retailer?.limited_ordering;

  const expandSidebar = () => {
    sidebar ? (sidebar = false) : (sidebar = true);
  };

  const openModal = () => {
    showModal = true;
  };

  const updateIsMobile = name => {
    isMobile = name == "mobile";
  };
</script>

<style lang="scss">
  .list-layout > [data-grid-area="sidebar-mobile"]:not(.fizz-is-hidden) {
    position: absolute;
    margin-top: 150px;
    background: rgba(0, 0, 0, 0.7);
    z-index: 3;
    width: 100%;
    height: 100%;
  }

  .fizz-layout-sidebar-left {
    grid-template-rows: 0 auto 0 1fr;
    grid-template-columns: minmax(260px, 260px);
    min-height: auto;
    @media (max-width: 1023px) {
      grid-template-columns: minmax(260px, 1fr);
    }
  }
</style>

<PageTitle title={`${list.name} - Lists | Provi`} />
<div
  class="list-layout fizz-layout-sidebar-left fizz-container"
  data-testid="quick-order-list-group"
  use:OnLayoutChange={updateIsMobile}
>
  <section data-grid-area="title" class="fizz-flex-row fizz-space-between">
    <h2 class="fizz-heading-3">Lists</h2>
    {#if !limitedOrderingRetailer}
      <button class="fizz-button-secondary" on:click={openModal}
        >{isMobile ? "Create a list" : "Create a new list"}</button
      >
    {/if}
  </section>
  <nav data-grid-area="sidebar-mobile" class:fizz-is-hidden={!isMobile || !sidebar}>
    <div class="sidebar-drawer fizz-surface fizz-inset-square-16">
      <Sidebar currentList={list} currentListType={listType} {lists} {retailer} />
    </div>
  </nav>
  <nav data-grid-area="sidebar" class="fizz-hide-on-small fizz-inset-square-32 fizz-border-right">
    <Sidebar currentList={list} currentListType={listType} {lists} {retailer} />
  </nav>
  <main data-grid-area="main">
    <Main
      {callbacks}
      {filters}
      {list}
      {listType}
      {page}
      {showLoading}
      {retailer}
      expanded={sidebar}
      numOfLists={lists.length}
      products={$QuickOrderListProductsStore}
      bind:productListEntity
      on:expandSidebar={expandSidebar}
      on:selectFilter
    />
  </main>
</div>
{#if showCarousel}
  {#key carouselKey}
    <div
      class="fizz-surface fizz-container-spacing fizz-inset-square-32 fizz-border-top"
      data-testid="quick-order-list-carousel"
    >
      <SelfFetchingCardCarousel
        endpoint={"list_carousel"}
        id={"stock_up_on_similar_products"}
        headerText={"Stock up on similar products"}
        pageName={"lists"}
        carouselDataProps={listAdParams}
        otherEntities={[productListEntity]}
        forwardLink={carouselLink}
      />
    </div>
  {/key}
{/if}

<NewListModal bind:showModal {newListName} />
